/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { closeDialog } from '../../../app/dialog/dialog.actions';
import { dialogSelectors } from '../../../app/dialog/dialog.selectors';
import { clearSetupSession, createSetupIntent } from '../../../app/page/subscription/subscription.actions';
import { subscriptionPageSelectors } from '../../../app/page/subscription/subscription.selectors';
import { getApiErrorMessage, useApiRequestState } from '../../../utils/api.utils';
import { useSelectWithParams } from '../../../utils/selector.utils';
import { SubmitButton } from '../../pages/common/forms/SubmitButton';
import { ConnectedDialog } from '../ConnectedDialog';
import { AddPaymentMethodForm } from './AddPaymentMethodForm';

export const AddPaymentMethodDialog: FunctionComponent = () => {
  const dispatch = useDispatch();
  const isOpen = useSelectWithParams(dialogSelectors.selectIsDialogOpen, 'addPaymentMethodDialog');
  const session = useSelector(subscriptionPageSelectors.selectSetupSession);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [isStripeReady, setIsStripeReady] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const hasBillingInfo = useSelector(subscriptionPageSelectors.selectHasBillingInfo);
  const collectBillingInfo = !hasBillingInfo;
  const [setupShowLoader, setupError] = useApiRequestState('createSetupIntent');
  const [createShowLoader, createError] = useApiRequestState('createPaymentMethod');
  const [saveBillingShowLoader, saveBillingError] = useApiRequestState('saveBillingInfo');

  const showLoader = setupShowLoader || createShowLoader || saveBillingShowLoader || isConfirming;
  let apiError = !setupShowLoader && setupError != null ? setupError : null;
  if (apiError == null && !createShowLoader && createError != null) {
    apiError = createError;
  }
  if (apiError == null && collectBillingInfo && !saveBillingShowLoader && saveBillingError != null) {
    apiError = saveBillingError;
  }
  const isFormReady = Boolean(session && stripePromise);

  const onClose = useCallback(() => {
    dispatch(closeDialog('addPaymentMethodDialog'));
    dispatch(clearSetupSession());
    setStripePromise(null);
    setIsStripeReady(false);
    setIsConfirming(false);
  }, [dispatch]);

  const onStripeReadyChange = useCallback((ready: boolean) => {
    setIsStripeReady(ready);
  }, []);

  const onConfirmingChange = useCallback((confirming: boolean) => {
    setIsConfirming(confirming);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    dispatch(createSetupIntent());
  }, [dispatch, isOpen]);

  const publishableKey = session?.publishableKey ?? null;

  useEffect(() => {
    if (!publishableKey) {
      setStripePromise(null);
      return;
    }

    setStripePromise(loadStripe(publishableKey));
  }, [publishableKey]);

  return (
    <ConnectedDialog dialogKey="addPaymentMethodDialog">
      <div className="popup new_team_popup mid-popup isVisible">
        <div className="panel">
          <div className="panel-header small-header flex jcsb">
            <h3>Add a Payment Method</h3>
            <span className="close-popup close">
              {showLoader && <Loader type="TailSpin" color="var(--theme-primary-color)" height={20} width={20} />}
              {apiError && !showLoader && (
                <p style={{ color: 'red' }}>{getApiErrorMessage(apiError) || 'Something went wrong'}</p>
              )}
            </span>
          </div>
          <div className="panel-body pt-0" style={{ maxHeight: '500px' }}>
            {setupShowLoader && !session && <div className="f-14 blueGray">Preparing secure card form...</div>}
            {isFormReady && (
              <Elements stripe={stripePromise}>
                <AddPaymentMethodForm
                  clientSecret={session!.clientSecret}
                  collectBillingInfo={collectBillingInfo}
                  onStripeReadyChange={onStripeReadyChange}
                  onConfirmingChange={onConfirmingChange}
                />
              </Elements>
            )}
          </div>

          <div className="panel-footer no-border flex jcr">
            <a className="link blueGray mr-2 close" title="" onClick={onClose}>
              Close
            </a>
            {isFormReady && (
              <SubmitButton
                apiKey="createPaymentMethod"
                formName="add-payment-method-form"
                text="Save"
                disabled={isConfirming || !isStripeReady}
              />
            )}
          </div>
        </div>
      </div>
    </ConnectedDialog>
  );
};
